import {
    CircularProgress,
    FormControl,
    FormControlProps,
    FormHelperText,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    SelectProps,
    Skeleton,
    useTheme,
} from '@mui/material';
import { ReactNode, useMemo } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import ModalErrorContainer from '@shared/components/modals/ModalErrorContainer';
import { useTranslation } from 'react-i18next';
import { ActionCreatorWithPayload, Selector } from '@reduxjs/toolkit';
import { RequestStatusEnum } from '@shared/types';

type Option<T = string> = {
    label: string;
    value: T;
};

interface UtmsSelectProps<T> {
    error?: boolean;
    formControlProps?: FormControlProps;
    label: string;
    selectProps?: SelectProps;
    value: string;
    onChange?: (event: SelectChangeEvent<unknown>, child?: ReactNode) => void;
    errorMessage?: string;
    options: Option<T>[];
    id: string;
    handleClear?: () => void;
    objectForFetch?: {
        getOptionsStatus: RequestStatusEnum;
        getOptionsReset: ActionCreatorWithPayload<any, string> | undefined;
        errorSelector: Selector;
        objectLabel: string;
    };
    renderValue?: (option: Option<T>) => ReactNode;
}

const UtmsSelect = <T,>({
    error,
    label,
    formControlProps,
    selectProps,
    value,
    onChange = () => {},
    errorMessage = '',
    options,
    handleClear,
    id,
    objectForFetch,
    renderValue,
}: UtmsSelectProps<T>) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const renderSkeletons = useMemo(() => {
        return [...Array(2)].map((_, i) => (
            <MenuItem key={i}>
                <Skeleton animation="wave" variant="text" width={'100%'} />
            </MenuItem>
        ));
    }, []);

    return (
        <>
            <FormControl size="small" error={error} {...formControlProps}>
                <InputLabel id={`${id}-select`}>{label}</InputLabel>
                <Select
                    labelId={`${id}-select`}
                    value={value}
                    label={label}
                    onChange={onChange}
                    endAdornment={
                        objectForFetch?.getOptionsStatus === RequestStatusEnum.Loading ? (
                            <InputAdornment position="start" sx={{ mr: 2 }}>
                                <CircularProgress size={18} />
                            </InputAdornment>
                        ) : (
                            Boolean(value?.length) &&
                            handleClear && (
                                <InputAdornment position="start" sx={{ mr: 2 }}>
                                    <IconButton size="small" onClick={handleClear}>
                                        <ClearIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        )
                    }
                    {...selectProps}
                    sx={{
                        '&.Mui-disabled': {
                            backgroundColor: theme.palette.action.hover,
                        },
                        ...selectProps?.sx,
                    }}
                >
                    {objectForFetch?.getOptionsStatus === RequestStatusEnum.Loading && renderSkeletons}
                    {objectForFetch?.getOptionsStatus === RequestStatusEnum.Success &&
                        (renderValue
                            ? options.map(renderValue)
                            : options.map((option) => {
                                  const { label } = option;

                                  return (
                                      <MenuItem key={label} value={label}>
                                          {label}
                                      </MenuItem>
                                  );
                              }))}
                </Select>
                {error && <FormHelperText>{errorMessage}</FormHelperText>}
            </FormControl>
            {objectForFetch && objectForFetch.getOptionsStatus === RequestStatusEnum.Error && (
                <ModalErrorContainer
                    object={objectForFetch.objectLabel}
                    action={t('fetching')}
                    resetActionCallback={objectForFetch.getOptionsReset}
                    errorSelector={objectForFetch.errorSelector}
                />
            )}
        </>
    );
};

export default UtmsSelect;
